import { useFlyOutProductsBasedOnSelectedCloud } from '../../../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import { faFilePlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AutoComplete,
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
  PlanType,
} from '@labrav/react-components'
import { useMemo } from 'react'
import { useStyles } from './CreateMeteringForm.styles'
import { useNavigate, useParams } from 'react-router-dom'
import { CloudAccountChangeV2, CloudAccountChangeParams } from '@labrav/common'
import {
  useCloudAccounts,
  getSelectedAccountIdWithCloud,
  extractAccountIdFromCombinedId,
  useAccountId,
  setSelectedAccount,
  CloudType,
  actionTypeWrapper,
  useCurrentCloudProvider,
  isLoading,
  LoadingTypes,
} from '@labrav/common'
import { useDispatch, useSelector } from '../../../../../store/hooks'
import { setCloudMarketplaceProductListing } from '../../../../../modules/productListingFilter/action'
import {
  setSelectedProductOnListing,
  getProducts,
} from '../../../../../modules/productsListing/action'
import * as Yup from 'yup'
import { Formik } from 'formik'
import { isEmpty } from 'lodash'
import { useFlag } from '@labrav/flags'

export default function CreateMeteringForm({
  showModal,
  updateModalVisibility,
}: {
  showModal: boolean
  updateModalVisibility: () => void
}) {
  return (
    <div data-testId="createMetering-meteringFormModal">
      <ModalConfirmV2
        title="Enter record using form"
        icon={<FontAwesomeIcon style={{ height: '16px' }} icon={faFilePlus} />}
        content={
          <CreateMeteringFormContent
            updateModalVisibility={updateModalVisibility}
          />
        }
        open={showModal}
        hideAcceptButton
        hideCancelButton
        onAccept={() => void 0}
        onDecline={() => updateModalVisibility()}
        variant={ModalSizeVariants.large}
      />
    </div>
  )
}

const CreateMeteringFormContent = ({
  updateModalVisibility,
}: {
  updateModalVisibility: () => void
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { accountId: accountIdFromParams } = useParams<{
    accountId?: string
  }>()
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const selectedAccountIdFromRedux = useSelector<string | undefined>(state => {
    const selectedAccountIds = state.accountSelection?.selectedAccountIds as
      | Record<string, Partial<Record<CloudType, string>>>
      | undefined
    const flyoutSelections =
      selectedAccountIds?.['FLYOUT'] || selectedAccountIds?.['flyout']
    if (flyoutSelections) {
      const clouds = Object.keys(flyoutSelections)
      if (clouds.length > 0) {
        return flyoutSelections[clouds[0] as CloudType] || ''
      }
    }
    return undefined
  })

  const accountId =
    selectedAccountIdFromRedux || accountIdFromParams || accountIdFromHook || ''

  // Always use AWS for usage metering
  const cloudMarketplace = selectedCloudTypeProductListing || 'AWS'

  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const productsLoading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.FETCH_PRODUCTS)
  )

  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')

  const { flag: flyoutAzureEnabledMetering } = useFlag(
    'azureFlyoutUsageMetering'
  )

  const marketplaceCloudAccounts = useMemo(
    () =>
      baseMarketplaceCloudAccounts.map(account => ({
        ...account,
        disabled:
          account.provider === 'GCP' ||
          (account.provider === 'AZURE' && !flyoutAzureEnabledMetering),
      })),
    [baseMarketplaceCloudAccounts, flyoutAzureEnabledMetering]
  )

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    cloudMarketplace
  )

  const updatedProducts = products
    .filter(product =>
      cloudMarketplace === 'AWS'
        ? product.productName &&
          [PlanType.SAAS_CONTRACT, PlanType.SAAS_SUBSCRIPTION].includes(
            product.productType as PlanType
          ) &&
          (product.productType !== PlanType.SAAS_CONTRACT ||
            (product.productDimensions &&
              product.productDimensions.some(
                dimension => dimension.isAdditionalUsageAllowed
              )))
        : true
    )
    .map(product => ({
      label: product.productName,
      key: product?.productId ?? '',
    }))
  const filteredProducts = sortProductsAlphabetically(updatedProducts)

  const { classes } = useStyles()

  const initialValues = {
    selectedProduct: { label: '', key: '' },
    selectedCloud: selectedCloudTypeProductListing,
  }
  const validationSchema = Yup.object({
    selectedProduct: Yup.object()
      .nullable()
      .required('Please select the product.'),
    selectedCloud: Yup.string(),
  })

  const handleCloudAccountChange = async (
    params: CloudAccountChangeParams,
    setFieldValue: (field: string, value: any) => void
  ) => {
    // Extract original accountId from the combined format "accountId-product-cloud"
    const originalAccountId = extractAccountIdFromCombinedId(params.accountId)
    const { cloud } = params

    // Always set to AWS since usage metering is AWS-only
    setFieldValue('selectedCloud', cloud)
    // Clear selected product in form when account changes
    setFieldValue('selectedProduct', { label: '', key: '' })

    const cloudPath = cloud.toLowerCase()
    const pathname = `/flyout/${cloudPath}/${originalAccountId}/usage-metering`
    navigate(pathname, { replace: true })

    await dispatch(
      setSelectedAccount(originalAccountId, 'flyout', cloud as CloudType)
    )

    await dispatch(setCloudMarketplaceProductListing(cloud))

    await dispatch(
      actionTypeWrapper(
        selectedCloudTypeProductListing,
        setSelectedProductOnListing(undefined)
      )
    )

    await dispatch(getProducts(originalAccountId, cloud))
  }

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount={true}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={() => void 0}
    >
      {formikprops => {
        const { values, errors, touched, handleBlur, setFieldValue } =
          formikprops

        const handleSubmit = () => {
          const cloudPath = cloudMarketplace.toLowerCase()
          navigate(
            `/flyout/${cloudPath}/${accountId}/products-listing/${values.selectedProduct.key}/contracts`
          )
        }
        const disableCheck =
          !isEmpty(errors) || isEmpty(values.selectedProduct?.label)
        return (
          <>
            <div className={classes.container}>
              <CloudAccountChangeV2
                cloudAccounts={marketplaceCloudAccounts}
                selectedAccountId={selectedAccountIdWithCloud}
                onChange={params =>
                  handleCloudAccountChange(params, setFieldValue)
                }
                width={171}
                size="medium"
              />
              <AutoComplete
                id={'selectedProduct'}
                label={'Select listing'}
                options={productsLoading ? [] : filteredProducts}
                disabled={productsLoading}
                value={values.selectedProduct?.label || ''}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'object') {
                    setFieldValue('selectedProduct', newValue)
                  }
                }}
                onBlur={handleBlur}
                error={
                  touched.selectedProduct && Boolean(errors.selectedProduct)
                }
                helperText={
                  touched.selectedProduct && (errors.selectedProduct as string)
                }
              />
            </div>
            <div className={classes.footer}>
              <ButtonV2
                className={classes.cancelButton}
                onClick={updateModalVisibility}
                styleType="outlined"
              >
                Cancel
              </ButtonV2>
              <ButtonV2
                disabled={!!disableCheck}
                onClick={() => handleSubmit()}
              >
                Submit
              </ButtonV2>
            </div>
          </>
        )
      }}
    </Formik>
  )
}
export const sortProductsAlphabetically = (
  products: Array<{ label: string; key: string }>
) => {
  return [...products].sort((a, b) => a.label.localeCompare(b.label))
}
